import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "assets/scss/components/bigCalendar.scss";

import React, { useState, useEffect } from "react";
import { useApp } from "context";
import { Custom, Modal } from "components";
import { BigCalendarEvent } from "./BigCalendarEvent";
import { Enums, Store } from "utils";
import { PairValue } from "types";
import { IAppointment, IEvent } from "interfaces";
import {
  Calendar,
  DayLayoutAlgorithm,
  DayLayoutFunction,
  EventProps,
  SlotInfo,
  momentLocalizer,
} from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";

const DnDCalendar = withDragAndDrop(Calendar);
const localizer = momentLocalizer(moment);

type Props = {
  opened?: boolean;
  selectedHealth360?: IAppointment | null;
  refresh: number;
};

export const BigCalendar = (props: Props) => {
  const { t, theme, user, service, locale } = useApp();

  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
  const [filter, setFilter] = useState({
    clinicHours: true,
    health360Hours: false,
    blocked: false,
    providers: false,
    appointments: false,
    other: false,
  });
  const [providers, setProviders] = useState<PairValue[]>([]);
  const [calendars, setCalendars] = useState<any[]>([]);
  const [backgroundEvents, setBackgroundEvents] = useState<IEvent[]>([]);
  const [eventModalOpened, setEventModalOpened] = useState(false);
  const [incrementComponent, setIncrementComponent] = useState(0);

  const handleFilterChange = (e: any, checked: boolean) => {
    setFilter({ ...filter, [e.target.name]: checked });
  };

  const filterEvents = (events: IEvent[]) => {
    return events.filter((e) => {
      if (e.eId === -1) {
        return true;
      } else if (e.title === Enums.EnumEventTitle.ClinicHours) {
        return filter.clinicHours;
      } else if (e.title === Enums.EnumEventTitle.Health360Hours) {
        return filter.health360Hours;
      } else if (e.title === Enums.EnumEventTitle.Blocked) {
        return filter.blocked;
      } else if (e.title === Enums.EnumEventTitle.ProviderAvailability) {
        return filter.providers;
      } else if (e.title === null) {
        return filter.other;
      } else if (
        [
          Enums.EnumEventTitle.Health360InClinic,
          Enums.EnumEventTitle.Health360Virtual,
        ].includes(e.title)
      ) {
        return filter.appointments;
      }
    });
  };

  useEffect(() => {
    // TODO: clinicId (QUBE Medical) and serviceId (Health 360) are hardcoded
    service.provider
      .findByClinicAndService(3, 12)
      .then((res) => setProviders(res));
  }, []);

  const minHours = 8;
  const maxHours = 20;

  const isClinic = Store.sessionStorage.isClinic();

  const { userId, username }: { userId: number; username: string } =
    Store.sessionStorage.get();

  const isOwnerEvent = (event: IEvent) =>
    userId === (isClinic ? event.clinicId : event.providerId);

  useEffect(() => {
    service.calendar.find().then((res) => {
      setCalendars(res.calendars);

      const followups = res.clinicEvents.filter(
        (e: any) => e.title === Enums.EnumEventTitle.Health360Virtual
      );
      const otherClinicEvents = res.clinicEvents.filter(
        (e: any) => e.title !== Enums.EnumEventTitle.Health360Virtual
      );

      setEvents(
        isClinic
          ? [...res.clinicEvents, ...res.providerEvents]
          : [...res.providerEvents, ...followups]
      );
      !isClinic && setBackgroundEvents(otherClinicEvents);
    });
  }, [incrementComponent]);

  const handleCloseEventModal = () => {
    setEventModalOpened(false);
    setIncrementComponent(incrementComponent + 1);
  };

  useEffect(() => {
    setIncrementComponent(incrementComponent + 1);
  }, [props.refresh]);

  const basicEvent = (slotInfo: SlotInfo): IEvent => ({
    eId: -1,
    title: isClinic ? null : Enums.EnumEventTitle.ProviderAvailability,
    notes: "",
    start: slotInfo.start,
    end: slotInfo.end,
    ...(isClinic
      ? { clinicId: userId, providerId: null }
      : { clinicId: -1, providerId: userId }),
    isPrimary: isClinic ? null : false,
    isConfirmed: isClinic ? null : false,
    appointmentId: null,

    owner: username,
  });

  const handleAddEvent = async (slotInfo: SlotInfo) => {
    const be = basicEvent(slotInfo);

    setEvents([...events, be]);
    setSelectedEvent(be);
  };

  const updateFilterAfterUpsert = (title: Enums.EnumEventTitle | null) => {
    if (title === null) {
      setFilter({ ...filter, other: true });
    } else if (title === Enums.EnumEventTitle.Blocked) {
      setFilter({ ...filter, blocked: true });
    } else if (title === Enums.EnumEventTitle.Health360Hours) {
      setFilter({ ...filter, health360Hours: true });
    } else if (title === Enums.EnumEventTitle.ClinicHours) {
      setFilter({ ...filter, clinicHours: true });
    } else if (
      [
        Enums.EnumEventTitle.Health360InClinic,
        Enums.EnumEventTitle.Health360Virtual,
      ].includes(title)
    ) {
      setFilter({ ...filter, appointments: true });
    } else if (title === Enums.EnumEventTitle.ProviderAvailability) {
      setFilter({ ...filter, providers: true });
    }
  };

  const handleInsertEvent = async (
    event: IEvent,
    ids?: { clinicId?: number; providerId?: number }
  ) => {
    const [status, response] = await service.calendar.insert(
      event.title,
      event.notes === "" ? null : event.notes,
      event.start,
      event.end,
      event.isPrimary,
      event.isConfirmed,
      event.appointmentId,
      ids
    );

    if (eventModalOpened) {
      if (status === Enums.EnumResponse.Success) {
        updateFilterAfterUpsert(event.title);
        handleCloseEventModal();
      }
    } else {
      setIncrementComponent(incrementComponent + 1);
    }
  };

  const handleUpdateEvent = async (event: IEvent) => {
    setEvents(
      events.map((e) => (e.eId !== event.eId ? e : { ...e, ...event }))
    );

    const [status, response] = await service.calendar.update(
      event.eId,
      event.title,
      event.notes === "" ? null : event.notes,
      event.start,
      event.end,
      event.isPrimary,
      event.isConfirmed,
      event.appointmentId
    );

    if (eventModalOpened) {
      if (status === Enums.EnumResponse.Success) {
        updateFilterAfterUpsert(event.title);
        handleCloseEventModal();
      }
    } else {
      setIncrementComponent(incrementComponent + 1);
    }
  };

  const handleUpsertEvent = (
    event: IEvent,
    ids?: { clinicId?: number; providerId?: number }
  ) => {
    event.eId === -1 ? handleInsertEvent(event, ids) : handleUpdateEvent(event);
  };

  const handleDeleteEvent = async (event: IEvent) => {
    const [status, response] = await service.calendar.delete(event.eId);

    if (eventModalOpened) {
      if (status === Enums.EnumResponse.Success) handleCloseEventModal();
    } else {
      setIncrementComponent(incrementComponent + 1);
    }
  };

  const eventComponent = (target: any) => {
    for (let i = 0; i < 3; i++) {
      if (target.classList.contains("rbc-event")) {
        return target;
      } else {
        target = target.parentElement;
      }
    }
    return target;
  };

  const handleSelectEvent = (
    event: Object,
    e: React.SyntheticEvent<HTMLElement, Event>
  ) => {
    // console.log(event, e);
    // console.log((e.target as any).className);
    if (isOwnerEvent(event as IEvent)) {
      setSelectedEvent(event as IEvent);
    }

    setSelectedEvent(event as IEvent);
    setEventModalOpened(true);
  };

  const handleStyle = (
    event: Object,
    start: Date,
    end: Date,
    isSelected: boolean
  ): { className?: string; style?: React.CSSProperties } => {
    const e = event as IEvent;

    if (isClinic) {
      const color =
        calendars.find(
          (cal) =>
            cal.clinicId === e.clinicId && cal.providerId === e.providerId
        )?.color ?? null;

      return color
        ? {
            className: e.owner,
            style: { backgroundColor: "#" + color },
          }
        : {};
    } else {
      return e.title === Enums.EnumEventTitle.Health360Virtual
        ? {}
        : e.providerId === null
        ? {
            className: e.owner,
            style: { backgroundColor: "rgba(213, 0, 0, 0.33)" },
          }
        : e.isConfirmed
        ? {
            className: "confirmed",
            style: { backgroundColor: "#0b8043" },
          }
        : e.isPrimary
        ? {
            className: "primary",
            style: { backgroundColor: "#c0ca33" },
          }
        : {};
    }
  };

  const isEventMovable = (event: IEvent) =>
    (isClinic && event.title !== Enums.EnumEventTitle.Health360InClinic) ||
    (!isClinic && isOwnerEvent(event) && !event.isConfirmed);

  const isEventDraggable = (event: object) => isEventMovable(event as IEvent);

  const handleEventDrop = async (args: any) => {
    const { event, start, end } = args;
    handleUpdateEvent({ ...event, start, end });
  };

  const isEventResizable = (event: object) => isEventMovable(event as IEvent);

  const handleEventResize = (args: any) => {
    const { event, start, end } = args;
    handleUpdateEvent({ ...event, start, end });
  };

  const customLayoutAlgorithm: DayLayoutFunction = ({
    events,
    minimumStartDifference,
    slotMetrics,
    accessors,
  }: {
    events: any[];
    minimumStartDifference: number;
    slotMetrics: any;
    accessors: any;
  }) => {
    const slots = (maxHours - minHours) * 2;
    const round = (x: number, y: number = 1e4) => Math.round(x * y) / y;

    return events.map((e) => {
      const start = round(e.start.getHours() + e.start.getMinutes() / 60, 2);
      const end = round(e.end.getHours() + e.end.getMinutes() / 60, 2);

      const top = ((2 * (start - minHours)) / slots) * 100 + "%";
      const height = ((2 * (end - start)) / slots) * 100 + "%";
      const left = 0 + "%";
      const width = 100 + "%";

      return {
        event: e,
        style: { top, height, left, width },
      };
    });
  };

  const mr = "1rem";

  return (
    <>
      {isClinic && (
        <div
          style={{
            border: "1px solid " + theme.disabled.backgroundColor,
            display: "inline-block",
            borderRadius: "16px",
            padding: "0.5rem",
            marginBottom: "1.5rem",
            zoom: "0.75",
          }}
        >
          <Custom.Checkbox
            sx={{ marginRight: mr }}
            label={"Clinic Hours"}
            name={"clinicHours"}
            checked={filter.clinicHours}
            onChange={handleFilterChange}
          ></Custom.Checkbox>
          <Custom.Checkbox
            sx={{ marginRight: mr }}
            label={"Health 360 Hours"}
            name={"health360Hours"}
            checked={filter.health360Hours}
            onChange={handleFilterChange}
          ></Custom.Checkbox>
          <Custom.Checkbox
            sx={{ marginRight: mr }}
            label={"Blocked"}
            name={"blocked"}
            checked={filter.blocked}
            onChange={handleFilterChange}
          ></Custom.Checkbox>
          <Custom.Checkbox
            sx={{ marginRight: mr }}
            label={"Providers"}
            name={"providers"}
            checked={filter.providers}
            onChange={handleFilterChange}
          ></Custom.Checkbox>
          <Custom.Checkbox
            sx={{ marginRight: mr }}
            label={"Appointments"}
            name={"appointments"}
            checked={filter.appointments}
            onChange={handleFilterChange}
          ></Custom.Checkbox>
          <Custom.Checkbox
            label={"Other"}
            name={"other"}
            checked={filter.other}
            onChange={handleFilterChange}
          ></Custom.Checkbox>
        </div>
      )}
      <DnDCalendar
        localizer={localizer}
        components={{
          event: (props: EventProps<object>) => (
            <BigCalendarEvent
              event={props.event as IEvent}
              handleOpenEventModal={(event: IEvent) => {
                event.eId === -1 &&
                  !eventModalOpened &&
                  setEventModalOpened(true);
              }}
            />
          ),
        }}
        events={isClinic ? filterEvents(events) : events}
        backgroundEvents={backgroundEvents}
        eventPropGetter={handleStyle}
        // startAccessor="start"
        // endAccessor="end"
        selectable
        onSelectSlot={handleAddEvent}
        onSelectEvent={handleSelectEvent}
        defaultView="week"
        draggableAccessor={isEventDraggable}
        onEventDrop={handleEventDrop}
        resizableAccessor={isEventResizable}
        onEventResize={handleEventResize}
        style={{ height: "36.5rem" }}
        min={new Date(1972, 0, 1, minHours, 0, 0, 0)}
        max={new Date(1972, 0, 1, maxHours, 0, 0, 0)}
        // dayLayoutAlgorithm={customLayoutAlgorithm}
      />

      <Modal.BigCalendar
        selectedEvent={selectedEvent}
        providers={providers}
        opened={eventModalOpened}
        onClose={handleCloseEventModal}
        handleUpsertEvent={handleUpsertEvent}
        handleDeleteEvent={handleDeleteEvent}
        selectedHealth360={props.selectedHealth360}
      />
    </>
  );
};
